<template>
  <form name="document-constructor-form" @submit.prevent="$emit('save')">
    <FormTemplatesFormMain
      :form="form"
      :disabled="isView"
      :skeleton="skeleton"
      :is-create="isCreate"
      class="my-4"
    />

    <FormTemplatesFormSheet
      v-for="(item, index) in form.sheets"
      :key="item.uuid"
      :form="item"
      :field-name="validateSheetFieldName(item)"
      :disabled="isView"
      :skeleton="skeleton"
      class="my-4 js-sheet"
      :upable="index > 0"
      :downable="form.sheets.length > 1 && form.sheets.length - 1 !== index"
      deleteable
      @sortable-positions-up="sortSheet(item, index, index - 1)"
      @sortable-positions-down="sortSheet(item, index, index + 1)"
      @delete="deleteSheet(item, index)"
      @copy="copySheet(item)"
    />

    <v-row>
      <v-col cols="auto">
        <v-input
          :error="$errors.has(name)"
          :error-messages="$errors.first(name)"
        />
      </v-col>
    </v-row>

    <button ref="button" type="submit" class="d-none" />
  </form>
</template>

<script>
import last from 'lodash/last'
import { getUuid } from '../utils'

import { createModalRemove } from '@/helpers/createModalRemove'
import BLayoutForm from '@/components/layouts/BLayoutForm.vue'
import FormTemplatesFormMain from './FormTemplatesForm/FormTemplatesFormMain.vue'
import FormTemplatesFormSheet, {
  createDefaultData as createDefaultDataSheet,
  createPrefillData as createPrefillDataSheet,
} from './FormTemplatesFormSheet.vue'
import uniqueId from 'lodash/uniqueId'


const createDefaultData = () => ({
  name: '',
  description: '',
  sheets: [],
})

export default {
  name: 'FormTemplatesForm',
  inject: ['$validator'],
  provide() {
    return this.copy
  },
  components: {
    FormTemplatesFormMain,
    FormTemplatesFormSheet,
  },
  extends: BLayoutForm,
  props: {
    name: {
      type: String,
      default: () => uniqueId('form-templates-form-'),
    },
    skeleton: Boolean,
    data: {
      type: Object,
      required: true,
      default: createDefaultData,
    },
  },
  data: (vm) => ({
    copy: {
      sheetTree: vm.sheetTree,
    },
  }),
  computed: {
    sheetTree() {
      const { form } = this
      const $childrenSheets = form.sheets.map((sheet) => {
        const data = {
          $parent: form,
          $children: [],
          $data: sheet,
          uuid: sheet.uuid,
          name: sheet.name,
          code: sheet.code,
        }

        const $childrenCells = sheet.cells.map((cell) => ({
          $parent: data,
          $children: [],
          $data: cell,
          uuid: cell.uuid,
          name: cell.name,
          code: cell.code,
        }))

        data.$children = $childrenCells

        return data
      })

      const allCells = $childrenSheets.map((_) => _.$children).flat()

      return {
        $children: $childrenSheets,
        $all_cells: allCells,
        name: form.name,
      }
    },
  },
  watch: {
    sheetTree: {
      immediate: true,
      handler(val) {
        const { sheetTree } = this.copy
        if (!sheetTree) {
          this.copy.sheetTree = val
          return
        }

        Object.entries(val).forEach(([key, value]) => {
          // Нужно для реативности !!!
          sheetTree[key] = value
        })
      },
    },
  },
  methods: {
    validateSheetFieldName(sheet) {
      return `sheet-${sheet.uuid}`
    },
    onBeforeSubmit() {
      this.$validator.errors.clear()
      if (this.form.sheets?.length < 1) {
        this.$validator.errors.add({
          field: this.name,
          msg: 'Необходимо добавить листы.',
        })
        return null
      }
      const sheetNames = {}
      const sheetCodes = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const sheet of this.form.sheets) {
        if (Object.prototype.hasOwnProperty.call(sheetNames, sheet.name)) {
          sheetNames[sheet.name].push(sheet)
        } else {
          sheetNames[sheet.name] = [sheet]
        }
        if (Object.prototype.hasOwnProperty.call(sheetCodes, sheet.code)) {
          sheetCodes[sheet.code].push(sheet)
        } else {
          sheetCodes[sheet.code] = [sheet]
        }

        if (sheet.cells?.length === 0) {
          this.$validator.errors.add({
            field: this.validateSheetFieldName(sheet),
            msg: 'Необходимо добавить ячейки.',
          })
          return null
        }
      }

      const names = []
      const codes = []

      Object.keys(sheetCodes).forEach((key) => {
        if (sheetCodes[key].length > 1) {
          codes.push(...sheetCodes[key])
        }
      })

      Object.keys(sheetNames).forEach((key) => {
        if (sheetNames[key].length > 1) {
          names.push(...sheetNames[key])
        }
      })

      if (names.length > 0 || codes.length > 0) {
        names.forEach((sheet) => {
          this.$validator.errors.add({
            field: this.validateSheetFieldName(sheet),
            msg: 'Название листа должно быть уникальным',
          })
        })
        codes.forEach((sheet) => {
          this.$validator.errors.add({
            field: this.validateSheetFieldName(sheet),
            msg: 'Код листа должен быть уникальным',
          })
        })
        return null
      }


      return this.onSubmit()
    },
    async addSheet() {
      const list = this.form.sheets
      const uuid = await getUuid()
      const uuid2 = await getUuid()
      const code = list.length ? list.length : 0
      const data = createDefaultDataSheet(+code + 1, uuid, uuid2)
      list.push(data)

      this.$validator.errors.remove(this.name)

      this.$nextTick(this.scrollToBottom)
    },
    async copySheet(item) {
      const sheetFrom = JSON.parse(JSON.stringify(item))
      const list = this.form.sheets
      const UUIDmap = {}
      const promises = sheetFrom.cells.map(async (cell) => {
        const oldUUID = cell.uuid
        // eslint-disable-next-line no-param-reassign
        cell.uuid = await getUuid()
        UUIDmap[oldUUID] = cell.uuid
      })
      await Promise.all(promises)
      const uuid = await getUuid()

      UUIDmap[sheetFrom.uuid] = uuid

      sheetFrom.cells.forEach((cell) => {
        // eslint-disable-next-line no-param-reassign
        cell.dep_list_cell_uuid = UUIDmap[cell.dep_list_cell_uuid] ? UUIDmap[cell.dep_list_cell_uuid] : null
        // eslint-disable-next-line no-param-reassign
        cell.dep_list_sheet_uuid = UUIDmap[cell.dep_list_sheet_uuid] ? UUIDmap[cell.dep_list_sheet_uuid] : null
      })

      let num = 1
      for (let i = 1; i < list.length + 1; i += 1) {
        const findName = (sheet) => sheet.name === `${sheetFrom.name} (${i})`
        if (!list.some(findName)) {
          num = i
          break
        }
      }
      const name = `${sheetFrom.name} (${num})`
      const code = list.length ? list.length : 0
      const newSheet = createPrefillDataSheet(
        uuid,
        +code + 1,
        name,
        sheetFrom.vertical,
        sheetFrom.required,
        sheetFrom.cells,
      )
      list.push(newSheet)

      this.$validator.errors.remove(this.name)

      this.$nextTick(this.scrollToBottom)
    },
    sortSheet(data, oldIndex, newIndex) {
      const list = this.form.sheets
      list.splice(oldIndex, 1, list[newIndex])
      list.splice(newIndex, 1, data)
    },
    async deleteSheet(data, index) {
      const propsData = {
        titleText: `Удалить лист "${data.name}" <small>(${data.code})</small>`,
      }

      const modal = await createModalRemove(this, propsData)
      const result = await modal.open()
      if (!result) return
      const list = this.form.sheets
      list.splice(index, 1)
    },
    async scrollToBottom() {
      const module = await import(
        /* webpackChunkName: "vue-scrollto" */
        'vue-scrollto'
      )

      const vueScrollTo = module.default
      vueScrollTo.scrollTo(
        last(this.$el.querySelectorAll('.js-sheet')),
        200,
      )
    },
  },
}
</script>
